import { useCallback, useState } from 'react'

import { type AgnosticShipper } from 'core/domain/AgnosticShipper'
import { RequestStatus } from 'core/domain/Request'
import { useAbortController } from 'core/ui/hooks/useAbortController'

import { getAgnosticShippers } from '../../use-cases/getAgnosticShippers'

type State = {
  status: RequestStatus
  shippers: Array<AgnosticShipper>
}

function getInitialState(): State {
  return {
    status: RequestStatus.Initial,
    shippers: [],
  }
}

export function useAgnosticShippers() {
  const [{ status, shippers }, setState] = useState<State>(getInitialState)
  const abortController = useAbortController()

  const fetchShippers = useCallback(async () => {
    try {
      setState({
        ...getInitialState(),
        status: RequestStatus.Pending,
      })

      const result = await getAgnosticShippers(abortController)

      setState({ shippers: result, status: RequestStatus.Done })
    } catch (error) {
      if (!abortController.signal?.aborted) {
        setState({
          ...getInitialState(),
          status: RequestStatus.Error,
        })
      }
    }
  }, [abortController])

  const hasFetchShippersError = status === RequestStatus.Error
  const isFetchingShippers = status === RequestStatus.Pending
  const isFetchShipperCompleted = status === RequestStatus.Done

  return {
    hasFetchShippersError,
    isFetchingShippers,
    isFetchShipperCompleted,
    shippers,
    fetchShippers,
  }
}
